import React from 'react';
import {
    Boxes,
    Activity,
    FileText,
    ArrowUp,
} from 'lucide-react';

type Instrument = {
    short: string;
    full: string;
    live?: boolean;
    /** When true, force a row break before this chip in the flex-wrap chip
     *  row. Used for layout — e.g. so a wide chip wraps onto the same row
     *  as "+N more" instead of squeezing onto row 1. */
    wrapBefore?: boolean;
};

export type TopicGroup = {
    key: string;
    title: string;
    blurb: string;
    icon: React.ReactNode;
    instruments: Instrument[];
    moreCount: number;
    /** Contextual question rendered as a heading in the plan workspace
     *  when the user presses this box. */
    prompt: string;
};

// All build templates share one copper treatment — they live inside
// the Build brand zone (Build card on Home is copper-anchored). Tile
// differentiation comes from icon + copy, not color. The pre-corrective
// rainbow-tone map is retired. Chips are decorative (read-only) so the
// chip-hover token is unused here — the previous CHIP_HOVER_CLASS was
// removed during the box-redesign merge.
const ICON_CLASS = 'w-4 h-4 text-[color:var(--accent-copper)]';
const TILE_CLASS =
    'from-[color:var(--accent-copper-tint)] border-[color:var(--accent-copper)]/30 ' +
    'hover:border-[color:var(--accent-copper)]/60';
const CHIP_BORDER_CLASS = 'border-[color:var(--accent-copper)]/40';

const TOPICS: TopicGroup[] = [
    {
        key: 'models',
        title: 'Quantitative models',
        blurb: 'Build models for capital, credit, liquidity, IRRBB and more.',
        icon: <Boxes className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'NMD',            full: 'Non-Maturing Deposits behavioural model (LCR outflow categories, NSFR ASF, bucketing)', live: true },
            { short: 'IRRBB',          full: 'Interest Rate Risk in the Banking Book — NII / EVE shocks, repricing gap' },
            { short: 'CVA',            full: 'Credit Valuation Adjustment — K_CVA, RWA_CVA, CS01, feeds C 25.00' },
            { short: 'FTP',            full: 'Funds Transfer Pricing — 5-component breakdown, business-line margin' },
            { short: 'Securitization', full: 'Securitisation capital treatment — SEC-IRBA / SEC-SA / SEC-ERBA, significant risk transfer', wrapBefore: true },
        ],
        moreCount: 12,
        prompt: 'What type of model do you want to build?',
    },
    {
        key: 'documents',
        title: 'Qualitative artifacts',
        blurb: 'Draft disclosures, policies, methodologies and governance documents.',
        icon: <FileText className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'Recovery Plan',           full: 'BRRD recovery plan — triggers, options, timeline' },
            { short: 'Risk Appetite Framework', full: 'Board-approved risk appetite statement with KRIs and limits' },
            { short: 'Pillar III',              full: 'Pillar 3 public disclosure — capital structure, exposures, governance, remuneration' },
        ],
        moreCount: 13,
        prompt: 'What type of documentation do you want to build?',
    },
    {
        key: 'stress',
        title: 'Stress & scenarios',
        blurb: 'Build stress tests for capital, liquidity, P&L and more.',
        icon: <Activity className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'ICAAP',               full: 'Internal Capital Adequacy Assessment — multi-year stressed ratios' },
            { short: 'ILAAP',               full: 'Internal Liquidity Adequacy Assessment — stressed LCR / NSFR / CFP' },
            { short: 'Reverse Stress Test', full: 'Reverse stress test — what scenario breaks the bank' },
        ],
        moreCount: 1,
        prompt: 'What type of stress test do you want to build?',
    },
];

interface BuildShowcaseProps {
    /** Called with the topic when the user presses a box. Triggers
     *  navigation into the plan workspace in draft mode (no project
     *  is created here — see App.tsx onSendMessage interceptor). */
    onBoxDraft?: (topic: TopicGroup) => void;
    /** When true, every box button is disabled. Used to lock presses
     *  while a previous box-press's lazy-create is in flight. */
    disabled?: boolean;
}

export default function BuildShowcase({ onBoxDraft, disabled = false }: BuildShowcaseProps) {
    return (
        <section className="mb-4" aria-labelledby="build-heading">
            {/* Section header */}
            <div className="flex items-baseline justify-between mb-2">
                <span
                    id="build-heading"
                    className="font-body text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-secondary"
                >
                    What you can build
                </span>
                <span className="font-mono text-[11px] text-fg-tertiary uppercase tracking-wider tabular-nums">
                    EU banking · 40+ build artefacts · traceable to source
                </span>
            </div>

            {/* Lead — the key by-product framing */}
            <p className="font-body text-[14px] leading-[1.6] text-fg-secondary max-w-[720px] mb-5">
                BARK builds filings, models, scenarios and validations from your own data — each
                result traceable cell-by-cell to the article it comes from.{' '}
                <span className="text-fg font-semibold">
                    Regulatory filing templates (COREP, FINREP, Pillar 3) ship with the relevant
                    model
                </span>
                {' '}— not as separate deliverables you pick.
            </p>

            {/* Topic grid — each box is a real <button>; chips are decorative <span>s. */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {TOPICS.map((topic) => (
                    <button
                        key={topic.key}
                        type="button"
                        onClick={() => onBoxDraft?.(topic)}
                        disabled={disabled || !onBoxDraft}
                        className={`group relative text-left flex flex-col p-4 pr-12 rounded-lg transition-all
                                   bg-gradient-to-br to-surface border hover:shadow-bark-md
                                   focus:outline-none focus-visible:border-[color:var(--accent-copper)] focus-visible:shadow-bark-md
                                   disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none
                                   ${TILE_CLASS}`}
                    >
                        {/* Icon + title */}
                        <div className="flex items-center gap-2 mb-1.5">
                            {topic.icon}
                            <h3 className="font-body text-[13px] font-semibold text-fg">
                                {topic.title}
                            </h3>
                        </div>

                        {/* Blurb */}
                        <p className="font-body text-[11px] leading-relaxed text-fg-secondary mb-3">
                            {topic.blurb}
                        </p>

                        {/* Decorative instrument chips — read-only labels, no tooltip, no hover. */}
                        <div className="flex flex-wrap gap-1.5 mt-auto">
                            {topic.instruments.map((i) => {
                                const baseClass = i.live
                                    ? `inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-surface border ${CHIP_BORDER_CLASS} font-mono text-[11px] text-fg tabular-nums`
                                    : 'inline-flex items-center px-2 py-0.5 rounded-sm bg-surface/70 font-mono text-[11px] text-fg tabular-nums';
                                return (
                                    <React.Fragment key={i.short}>
                                        {i.wrapBefore && (
                                            // Zero-height full-width spacer to force a flex-wrap
                                            // row break. The `flex: 0 0 100%` shorthand pins
                                            // grow=0, shrink=0, basis=100% so the spacer cannot
                                            // shrink to fit alongside neighbours — forcing the
                                            // following chip onto a new row. Earlier attempts
                                            // with just basis-full / flexBasis 100% were defeated
                                            // by the default flex-shrink: 1 on flex items.
                                            <div
                                                aria-hidden="true"
                                                style={{
                                                    flex: '0 0 100%',
                                                    height: 0,
                                                    margin: 0,
                                                    padding: 0,
                                                }}
                                            />
                                        )}
                                        <span className={baseClass}>
                                            {i.short}
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                            {topic.moreCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 font-mono text-[11px] text-fg-tertiary tabular-nums">
                                    +{topic.moreCount} more
                                </span>
                            )}
                        </div>

                        {/*
                            Visual parity with ProjectSeedComposer's send button (see
                            ProjectSeedComposer.tsx:126-136 for the canonical class string).
                            Rendered as a non-interactive <span aria-hidden="true">, NOT a real
                            <button> — the outer button owns the click. Keeping this as a span
                            avoids the focusable-but-dead-button antipattern and keeps screen
                            readers announcing one button per box.
                        */}
                        <span
                            aria-hidden="true"
                            className="absolute bottom-3 right-3 w-[30px] h-[30px] flex items-center justify-center bg-accent text-white rounded-md transition-colors group-hover:bg-accent-hover group-disabled:bg-border-strong"
                        >
                            <ArrowUp className="w-4 h-4" strokeWidth={2} />
                        </span>
                    </button>
                ))}
            </div>
        </section>
    );
}
